/**
 * v2 ONU detail — deep-link target for one ONU on a GPON chassis.
 *
 * URL: /v2/chassis/:chassisId/onu/:port/:displayId
 *
 * Bundles the optical-history time series + MAC history + lifecycle
 * events on a single page. Mostly read-only; v1 destructive actions
 * (move / deprovision / swap) live on the chassis-page Maintenance
 * collapsible.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../../../api/client";
import { LocalTime, formatLocalShort } from "../../../lib/datetime";
import { CopyableCell } from "../../components/CopyableCell";
import { HelpButton } from "../../help/HelpPanel";

type OpticalSample = {
  ts: string;
  rx_power_dbm: number | null;
  tx_power_dbm: number | null;
  temperature_c: number | null;
  voltage_v: number | null;
};
type OpticalSeriesResponse = {
  samples: OpticalSample[];
  bucket: string;
  since: string;
  until: string;
};

type MacEntry = {
  mac_address: string;
  mac_normalised: string;
  vnet_or_vlan: string | null;
  collected_at: string;
};
type OnuMacs = {
  port: string;
  display_id: string;
  macs: MacEntry[];
};
type MacsResponse = { onus: OnuMacs[] };

export function OnuDetailPage() {
  const { chassisId, port, displayId } = useParams<{
    chassisId: string;
    port: string;
    displayId: string;
  }>();

  const optical = useQuery<OpticalSeriesResponse>({
    queryKey: ["v2", "onu", chassisId, port, displayId, "optical"],
    queryFn: async () => {
      const res = await api.get<OpticalSeriesResponse>(
        `/chassis/${chassisId}/onus/${encodeURIComponent(port ?? "")}/${encodeURIComponent(displayId ?? "")}/optical-history?bucket=hour`,
      );
      return res.data;
    },
  });

  const macs = useQuery<MacsResponse>({
    queryKey: ["v2", "chassis", chassisId, "macs"],
    queryFn: async () => {
      const res = await api.get<MacsResponse>(`/chassis/${chassisId}/macs`);
      return res.data;
    },
  });

  // Locate this ONU's MAC list + pick a serial for the lifecycle
  // lookup (the inventory page in v2 doesn't carry the serial down
  // to the OnuDetail URL; we pull it from the optical / macs joins).
  const onuMacs =
    macs.data?.onus.find((o) => o.port === port && o.display_id === displayId)
      ?.macs ?? [];

  return (
    <section className="flex flex-col gap-3">
      <Link
        to={`/chassis/${chassisId}#tab=inventory`}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
      >
        <ChevronLeft className="h-3 w-3" />
        Back to chassis
      </Link>
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-semibold text-slate-100">
          ONU{" "}
          <CopyableCell value={`${port}/${displayId}`} />
        </h1>
        <HelpButton topic="onu-detail" />
      </header>

      {/* Optical chart — rx + tx dBm over the last 7 days */}
      {(optical.data?.samples.length ?? 0) > 1 && (
        <section className="rounded-lg bg-slate-900 shadow-border p-4">
          <h2 className="mb-2 text-sm font-medium text-slate-200">
            RX / TX dBm trend ({optical.data!.samples.length} samples)
          </h2>
          <OpticalChart samples={optical.data!.samples} />
        </section>
      )}

      {/* Optical samples */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">
          Optical samples (hour buckets, last 7 days)
        </h2>
        {optical.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : (optical.data?.samples.length ?? 0) === 0 ? (
          <p className="text-xs text-slate-500">
            No optical samples. Run <em>Collect optical</em> from the
            chassis page Run-a-job dropdown.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">When</th>
                <th className="px-2 py-1 text-right">RX dBm</th>
                <th className="px-2 py-1 text-right">TX dBm</th>
                <th className="px-2 py-1 text-right">Temp °C</th>
                <th className="px-2 py-1 text-right">V</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {(optical.data?.samples ?? []).slice(-50).reverse().map((s, i) => (
                <tr key={i}>
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={s.ts} />
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {s.rx_power_dbm?.toFixed(2) ?? "—"}
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {s.tx_power_dbm?.toFixed(2) ?? "—"}
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {s.temperature_c?.toFixed(1) ?? "—"}
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {s.voltage_v?.toFixed(2) ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* MAC history */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">
          MAC history ({onuMacs.length})
        </h2>
        {macs.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : onuMacs.length === 0 ? (
          <p className="text-xs text-slate-500">
            No MACs collected. Run <em>Collect MAC table</em> from the
            chassis page.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">MAC</th>
                <th className="px-2 py-1">VLAN</th>
                <th className="px-2 py-1">Collected</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {onuMacs.slice(0, 20).map((m, i) => (
                <tr key={i}>
                  <td className="px-2 py-1">
                    <CopyableCell value={m.mac_normalised} />
                  </td>
                  <td className="px-2 py-1 font-mono">
                    {m.vnet_or_vlan ?? "—"}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={m.collected_at} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </section>
  );
}

function OpticalChart({ samples }: { samples: OpticalSample[] }) {
  // RX / TX dBm trend. dBm is signed (typically -8 to -30); the
  // chart auto-scales. ``connectNulls={false}`` so a polling gap
  // doesn't draw a misleading line through it.
  const data = samples.map((s) => ({
    ts: formatLocalShort(s.ts),
    rx: s.rx_power_dbm,
    tx: s.tx_power_dbm,
  }));
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis
            dataKey="ts"
            tick={{ fill: "#64748b", fontSize: 10 }}
            stroke="#334155"
          />
          <YAxis
            tick={{ fill: "#64748b", fontSize: 10 }}
            stroke="#334155"
            unit=" dBm"
            width={70}
          />
          <Tooltip
            contentStyle={{
              background: "#0f172a",
              border: "1px solid #334155",
              fontSize: 11,
            }}
            labelStyle={{ color: "#94a3b8" }}
            formatter={(value, name) => [
              typeof value === "number" ? `${value.toFixed(2)} dBm` : "—",
              name === "rx" ? "RX" : "TX",
            ]}
          />
          <Line
            type="monotone"
            dataKey="rx"
            stroke="#34d399"
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="tx"
            stroke="#60a5fa"
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
